import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      return String(first);
    }
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.detail === 'object') return JSON.stringify(data.detail);
    const fieldErrors = Object.keys(data).filter(key => key !== 'detail');
    if (fieldErrors.length > 0) {
      const field = fieldErrors[0];
      const message = data[field];
      return `${field}: ${typeof message === 'string' ? message : JSON.stringify(message)}`;
    }
  }
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  if (data.message) return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
  return `Request failed with status ${status}`;
};

const InvoiceFormSimple = ({ onClose, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const getDateString = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  const addDays = (date, days) => { const result = new Date(date); result.setDate(result.getDate() + days); return result; };

  const today = new Date();
  const defaultDueDate = addDays(today, 30);

  const [formData, setFormData] = useState({
    customer_id: '',
    issue_date: getDateString(today),
    due_date: getDateString(defaultDueDate),
    gst_rate: '0',
    notes: ''
  });
  const [items, setItems] = useState([{ product_id: '', unit_id: '', quantity: '1.00', unit_price: 0, discount_percent: 0 }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchCustomers = async () => {
    try { const r = await api.get('/customers?is_active=true'); setCustomers(r.data.items ?? r.data); } catch (e) { console.error(e); }
  };
  const fetchProducts = async () => {
    try { const r = await api.get('/products?limit=1000'); const items = r.data.items ?? r.data.products ?? []; setProducts(items.filter(p => p.is_active)); } catch (e) { console.error(e); }
  };

  const handleProductChange = (index, productId) => {
    const newItems = [...items];
    newItems[index].product_id = productId;
    newItems[index].unit_id = '';
    const product = products.find(p => p.id === productId);
    if (product) {
      const defaultUnit = (product.units || []).find(u => u.is_default);
      if (defaultUnit) {
        newItems[index].unit_id = defaultUnit.id;
        newItems[index].unit_price = parseFloat(defaultUnit.selling_price || product.selling_price || 0);
      } else {
        newItems[index].unit_price = parseFloat(product.selling_price);
      }
    }
    setItems(newItems);
  };

  const handleUnitChange = (index, unitId) => {
    const newItems = [...items];
    newItems[index].unit_id = unitId;
    const product = products.find(p => p.id === newItems[index].product_id);
    if (product) {
      if (unitId) {
        const unit = (product.units || []).find(u => u.id === unitId);
        if (unit) newItems[index].unit_price = parseFloat(unit.selling_price || product.selling_price || 0);
      } else {
        newItems[index].unit_price = parseFloat(product.selling_price || 0);
      }
    }
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { product_id: '', unit_id: '', quantity: '1.00', unit_price: 0, discount_percent: 0 }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const calculateSubtotal = () => items.reduce((sum, item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discount = subtotal * ((item.discount_percent || 0) / 100);
    return sum + (subtotal - discount);
  }, 0);
  const calculateGst = () => { const rate = parseFloat(formData.gst_rate); if (!rate) return 0; return calculateSubtotal() * (rate / 100); };
  const calculateTotal = () => calculateSubtotal() + calculateGst();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.customer_id) { setError('Please select a customer'); return; }
    if (items.length === 0 || !items[0].product_id) { setError('Please add at least one item'); return; }
    for (let i = 0; i < items.length; i++) {
      if (!items[i].product_id) { setError(`Item ${i + 1}: Please select a product`); return; }
      if (!items[i].quantity || parseFloat(items[i].quantity) <= 0) { setError(`Item ${i + 1}: Quantity must be greater than 0`); return; }
      if (!items[i].unit_price || parseFloat(items[i].unit_price) < 0) { setError(`Item ${i + 1}: Unit price cannot be negative`); return; }
    }
    setLoading(true);
    try {
      await api.post('/invoices', {
        customer_id: formData.customer_id,
        issue_date: formData.issue_date || getDateString(new Date()),
        due_date: formData.due_date || getDateString(addDays(new Date(formData.issue_date || new Date()), 30)),
        notes: formData.notes || null,
        gst_rate: formData.gst_rate !== '' ? parseFloat(formData.gst_rate) : null,
        items: items.map(item => ({
          product_id: item.product_id,
          unit_id: item.unit_id || null,
          quantity: item.quantity,
          unit_price: parseFloat(item.unit_price),
          discount_percent: parseFloat(item.discount_percent) || 0
        }))
      });
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Invoice creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const itemInputClass = 'w-full bg-white border border-[#d1d5db] rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none';

  return (
    <ModalShell
      title="Create Invoice"
      onClose={onClose}
      maxWidth="max-w-5xl"
      dataTestId="invoice-form-modal"
      footer={
        <div className="flex justify-between items-center">
          <div className="text-sm">
            <span className="text-[#6b7280]">Total: </span>
            <span className="text-xl font-bold text-[#4A7AE0]" data-testid="total-amount">Rs. {calculateTotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-button">Cancel</button>
            <button type="submit" form="invoice-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="save-invoice-button">{loading ? 'Creating...' : 'Create Invoice'}</button>
          </div>
        </div>
      }
    >
      <form id="invoice-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="form-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Customer {requiredMark}</label>
            <select className={selectClass} value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} required data-testid="customer-select">
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Issue Date</label>
            <input type="date" className={inputClass} value={formData.issue_date} onChange={(e) => {
              const newIssueDate = e.target.value;
              const newDueDate = addDays(new Date(newIssueDate), 30);
              setFormData({ ...formData, issue_date: newIssueDate, due_date: getDateString(newDueDate) });
            }} data-testid="issue-date-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Due Date <span className="text-xs text-[#6b7280] font-normal">(defaults to 30 days)</span></label>
            <input type="date" className={inputClass} value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} data-testid="due-date-input" />
          </div>
          <div>
            <label className={labelClass}>GST %</label>
            <input type="number" min="0" max="100" step="0.01" placeholder="Tenant default" className={inputClass} value={formData.gst_rate} onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })} data-testid="gst-rate-input" />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-semibold text-[#1a1a2e]">Items</h3>
            <button type="button" onClick={addItem} className="text-[#4A7AE0] hover:underline text-sm font-medium flex items-center gap-1" data-testid="add-item-button">
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-2">
            {items.map((item, index) => {
              const selectedProduct = products.find(p => p.id === item.product_id);
              const productUnits = selectedProduct?.units || [];
              const baseUnitLabel = selectedProduct?.base_unit || selectedProduct?.unit || '';
              return (
                <div key={index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <select className={itemInputClass} value={item.product_id} onChange={(e) => handleProductChange(index, e.target.value)} required data-testid={`product-select-${index}`}>
                      <option value="">Select Product</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name} (Stock: {p.current_stock} {p.base_unit || p.unit})</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <select className={`${itemInputClass} disabled:bg-[#f3f4f6]`} value={item.unit_id} onChange={(e) => handleUnitChange(index, e.target.value)} disabled={!item.product_id} data-testid={`unit-select-${index}`}>
                      <option value="">{baseUnitLabel || 'Base unit'}</option>
                      {productUnits.map(u => <option key={u.id} value={u.id}>{u.unit_name} ({u.conversion_factor} {baseUnitLabel})</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0.01" placeholder="Qty" className={itemInputClass} value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} required data-testid={`quantity-input-${index}`} />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0" placeholder="Price" className={itemInputClass} value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} required data-testid={`price-input-${index}`} />
                  </div>
                  <div className="col-span-1">
                    <input type="number" step="0.01" min="0" max="100" placeholder="Disc%" className={itemInputClass} value={item.discount_percent} onChange={(e) => handleItemChange(index, 'discount_percent', e.target.value)} data-testid={`discount-input-${index}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)} className="text-[#EF4444] hover:bg-[#FEE2E2] p-1.5 rounded-lg" data-testid={`remove-item-${index}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-4 p-3 bg-white rounded-lg border border-[#d1d5db] space-y-1.5">
          <div className="flex justify-between text-sm text-[#6b7280]">
            <span>Subtotal</span>
            <span data-testid="invoice-subtotal">Rs. {calculateSubtotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-sm text-[#6b7280]">
            <span>GST {formData.gst_rate ? `(${formData.gst_rate}%)` : '(tenant default)'}</span>
            <span data-testid="invoice-gst-amount">Rs. {calculateGst().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div>
          <label className={labelClass}>Notes</label>
          <textarea className={textareaClass} rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" placeholder="Additional notes or terms..." />
        </div>
      </form>
    </ModalShell>
  );
};

export default InvoiceFormSimple;
